import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import type { PimsRecordRef } from "@pims/pimsActivityClient";

/**
 * One step of a completed PIMS write's on-screen journal (WEB-3237), projected
 * from the backend `EvidenceStep`. `screenshotUrl` is a short-lived presigned
 * GET (~1h) for the frame this step captured, or null when it captured none.
 */
export interface PimsAuditStep {
  index: number;
  kind: string;
  step: number | null;
  text: string | null;
  action: Record<string, unknown> | null;
  /** Leaf filename of the frame this step captured (for de-duping orphans). */
  screenshot: string | null;
  screenshotUrl: string | null;
}

/** A presigned screenshot from the write's run dir, keyed by filename. */
export interface PimsAuditScreenshot {
  name: string;
  url: string;
}

/**
 * One labelled span of a task's session recording (PIMS session replay POC),
 * projected from the backend `ReplayChapterOut`. Offsets are milliseconds from
 * recorder start — `startedMs / 1000` is the seek target for the native
 * `<video>` element.
 */
export interface PimsReplayChapter {
  index: number;
  stepId: number | null;
  label: string;
  startedMs: number;
  endedMs: number;
  outcome: "done" | "failed";
  fieldsTouched: string[];
  error: string | null;
}

export type FailureImpact = "recovered" | "contributing" | "terminal";
export type FailureOutcome = "failed" | "succeeded_with_recovery";

/**
 * A clinic-safe incident from a failed/recovered PIMS write, projected from the
 * backend `FailureIncidentOut` (WEB-3272). Whitelist-mapped so no diagnostic
 * content ever reaches the view model, and `impact` is narrowed to the known
 * vocabulary — an unrecognised impact drops the whole incident.
 */
export interface PimsFailureIncident {
  label: string;
  category: string;
  explanation: string;
  impact: FailureImpact;
  failedStepIds: number[];
  successfulRetryStepId: number | null;
}

/**
 * The audit trail for one completed PIMS write: the step journal plus the
 * screenshots captured while doing it, from `GET /hans_tasks/{uuid}/evidence`.
 * `summary` is the backend's opaque terminal-result extras — read defensively.
 * `hasTrace` is false for non-computer-use writes, which ship a summary only.
 *
 * The replay fields project the backend's flattened `video_url`/`capture_*`/
 * `chapters`/`intent`/`record` — see `HansTaskEvidenceOut` for the null-vs-
 * "invalid" `captureStatus` distinction. A null `videoUrl` means no video at all
 * (legacy task or recorder failure); the UI falls back to the screenshot audit.
 */
export interface PimsAudit {
  taskId: string;
  goal: string;
  status: string;
  output: string | null;
  summary: Record<string, unknown> | null;
  steps: PimsAuditStep[];
  screenshots: PimsAuditScreenshot[];
  hasTrace: boolean;
  videoUrl: string | null;
  captureStatus: string;
  captureError: string | null;
  durationMs: number | null;
  failedChapterIndex: number | null;
  chapters: PimsReplayChapter[];
  intent: { goal: string; writeMode: string | null };
  record: PimsRecordRef | null;
  failureWhy: string | null;
  failureOutcome: FailureOutcome | null;
  failureCategory: string | null;
  /** Mapped through but not rendered in v0. */
  failureConfidence: number | null;
  failureCausalStepId: number | null;
  failureIncidents: PimsFailureIncident[];
}

type Evidence = components["schemas"]["HansTaskEvidenceOut"];

const IMPACTS: FailureImpact[] = ["recovered", "contributing", "terminal"];

/** Whitelist-map one backend incident. Returns null (dropping it) when the
 * `impact` is outside the known vocabulary, so an unrecognised future value
 * never reaches the UI and no unmapped/diagnostic field can leak. */
const toIncident = (
  raw: NonNullable<Evidence["failure_incidents"]>[number],
): PimsFailureIncident | null => {
  const impact = IMPACTS.find((i) => i === raw.impact) ?? null;
  if (!impact) return null;
  return {
    label: raw.label ?? "",
    category: raw.category ?? "unknown",
    explanation: raw.explanation ?? "",
    impact,
    failedStepIds: (raw.failed_step_ids ?? []).filter(
      (n): n is number => typeof n === "number",
    ),
    successfulRetryStepId: raw.successful_retry_step_id ?? null,
  };
};

const toStep = (s: components["schemas"]["EvidenceStep"]): PimsAuditStep => ({
  index: s.index,
  kind: s.kind,
  step: s.step ?? null,
  text: s.text ?? null,
  action: s.action ?? null,
  screenshot: s.screenshot ?? null,
  screenshotUrl: s.screenshot_url ?? null,
});

/** A chapter's `outcome` is a lenient backend `str` — anything but `"failed"`
 * degrades to `"done"` so an unrecognised future outcome still renders as a
 * normal (non-error) chapter rather than breaking the player. */
const toOutcome = (outcome: string): "done" | "failed" =>
  outcome === "failed" ? "failed" : "done";

const toChapter = (
  c: components["schemas"]["ReplayChapterOut"],
): PimsReplayChapter => ({
  index: c.index,
  stepId: c.step_id ?? null,
  label: c.label,
  startedMs: c.started_ms,
  endedMs: c.ended_ms,
  outcome: toOutcome(c.outcome),
  fieldsTouched: c.fields_touched,
  error: c.error ?? null,
});

const toAudit = (
  e: components["schemas"]["HansTaskEvidenceOut"],
): PimsAudit => ({
  taskId: e.task_uuid,
  goal: e.goal,
  status: e.status,
  output: e.output,
  summary: e.summary,
  steps: (e.timeline ?? []).map(toStep),
  screenshots: e.screenshots,
  hasTrace: e.has_trace,
  videoUrl: e.video_url ?? null,
  captureStatus: e.capture_status ?? "unavailable",
  captureError: e.capture_error ?? null,
  durationMs: e.duration_ms ?? null,
  failedChapterIndex: e.failed_chapter_index ?? null,
  chapters: (e.chapters ?? []).map(toChapter),
  // guards accept off-contract backends (missing chapters, intent: null)
  intent: {
    goal: e.intent?.goal ?? "",
    writeMode: e.intent?.write_mode ?? null,
  },
  record: e.record
    ? { kind: e.record.kind, uuid: e.record.uuid, name: e.record.name ?? "" }
    : null,
  failureWhy: e.failure_why ?? null,
  failureOutcome:
    e.failure_outcome === "failed" ||
    e.failure_outcome === "succeeded_with_recovery"
      ? e.failure_outcome
      : null,
  failureCategory: e.failure_category ?? null,
  failureConfidence: e.failure_confidence ?? null,
  failureCausalStepId: e.failure_causal_step_id ?? null,
  failureIncidents: (e.failure_incidents ?? [])
    .map(toIncident)
    .filter((i): i is PimsFailureIncident => i !== null),
});

export const pimsAuditClient = {
  /**
   * Fetch the audit for one write by its task uuid (the same `id` the activity
   * view already shows). Resolves to `null` — not an error — when the write has
   * no exported artifacts (backend 404), so the UI can say "no audit available"
   * rather than surfacing an error. Every call re-signs the presigned URLs, so
   * an expired-URL refresh is just a refetch.
   */
  get: (taskId: string): Promise<PimsAudit | null> =>
    request(() =>
      apiClient.GET("/pims/tasks/{task_uuid}/evidence", {
        params: { path: { task_uuid: taskId } },
      }),
    )
      .then(toAudit)
      .catch((error) => {
        if (error instanceof ClinicAuthError && error.status === 404)
          return null;
        throw error;
      }),
};
